import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useQuery as useApolloQuery } from '@apollo/client';
import { ChevronLeft, Download, Eye, CheckCircle, XCircle, Calendar, MapPin, Image as ImageIcon, ChevronRight, ChevronLeft as ChevronLeftIcon, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { PROOF_OF_PLAY, PROOF_OF_PLAY_ASSERTS } from '../Graphql/Queries';
import { generateMockData } from '../Graphql/MockData';

interface ProofOfPlayItem {
  isVideo: any;
  previewUrl: string | undefined;
  id: string;
  campaignId: string;
  billboardId: string;
  creativeId: string;
  creativeName: string;
  photoUrl: string;
  displayDateTime: string;
  screenLocation: string;
  status: "submitted" | "approved" | "rejected";
  submittedBy: string;
  uploadedAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
  inventoryType?: string;
  comments?: string;
}

// New interfaces for API response
interface ProofOfPlayImage {
  isVideo: any;
  previewUrl: string | undefined;
  id: string;
  campaignId?: string;
  billboardId: string;
  creativeId: string;
  creativeName: string;
  photoUrl: string;
  displayDateTime: string;
  displayDate: string;
  uploadDate: string;
  screenLocation: string;
  status: string;
  submittedBy: string;
  uploadedAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
}

interface CampaignDetails {
  id: string;
  name: string;
  dealId: string;
  duration: string;
  startDate: string;
  endDate: string;
}

interface ProofInventory {
  inventoryName: string;
  id: string;
  inventoryId: string;
  inventoryType: string;
  mediaOwnerName?: string;
  mediaOwnerId?: string;
  packageName?: string;
  packageId?: string;
}

interface ProofValues {
  expected: number;
  submitted: number;
  approved: number;
  rejected: number;
}

interface ProofOfPlayInterface {
  base_path: string;
}

const _inventoryTypeDisplayMapping: { [key: string]: string } = {
  'digital': 'Digital',
  'digital_billboard': 'Digital',
  'classic': 'Classic',
  'static': 'Classic',
  'static_billboard': 'Classic',
  'deal': 'Deal'
};

export default function ProofOfPlay({ base_path }: ProofOfPlayInterface) {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Debug component lifecycle
  useEffect(() => {
    console.log('ProofOfPlay component mounted, location:', location.pathname);
    return () => {
      console.log('ProofOfPlay component unmounting');
    };
  }, [location.pathname]);

  // Debug the URL params first (only log once on mount)
  useEffect(() => {
    console.log('URL Params Debug:', {
      campaignId: campaignId,
      urlPath: window.location.pathname,
      urlSearchParams: window.location.search,
      base_path: base_path,
      base_path_env: import.meta.env.VITE_APP_BASE_PATH,
      expected_my_campaigns_path: `${base_path}/my-campaigns`
    });
  }, []); // Only run once on mount

  // Authentication and environment variables (matching campaigns page approach)
  const token = localStorage.getItem('authToken');
  const userId = localStorage.getItem('userId');
  const companyId = localStorage.getItem('companyId');

  const [selectedCampaign, setSelectedCampaign] = useState<string>(campaignId || "1");
  const [selectedBillboard, setSelectedBillboard] = useState<string>("all");
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<ProofOfPlayItem | null>(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [selectedItemForReject, setSelectedItemForReject] = useState<ProofOfPlayItem | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const [mediaFilter, setMediaFilter] = useState<"all" | "images" | "videos">("all");
  const [inventoryTypeFilter, setInventoryTypeFilter] = useState<string>("all");
  const [selectedPackage, setSelectedPackage] = useState<string>("all");
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  // New state for API integration
  const [campaign, setCampaign] = useState<CampaignDetails | null>(null);
  const [proofImages, setProofImages] = useState<ProofOfPlayImage[]>([]);
  const [filteredImages, setFilteredImages] = useState<ProofOfPlayImage[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorData, setErrorData] = useState<string | null>(null);
  const [errorAssets, setErrorAssets] = useState<string | null>(null);
  const [proofValues, setProofValues] = useState<ProofValues | null>(null);
  const [billboards, setBillboards] = useState<ProofInventory[]>([]);

  const queryClient = useQueryClient();

  // Debug authentication data (only log once on mount)
  const hasRealAuth = token && userId && companyId;
  useEffect(() => {
    console.log('Auth Debug (initial load):', {
      token: token ? 'exists' : 'missing',
      companyId: companyId || 'missing',
      userId: userId ? 'exists' : 'missing',
      campaignId: campaignId || 'missing',
      hasRealAuth: hasRealAuth,
      mockDataEnabled: import.meta.env.VITE_APP_MOCK_DATA === 'true'
    });
  }, []); // Only run once on mount
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  // Apollo GraphQL queries for real data (FORCE EXECUTION TO SEE MISSING PARAMETERS)
  const { data, loading, error, refetch } = useApolloQuery(PROOF_OF_PLAY, {
    variables: {
      accessToken: token,
      page: 0,
      campaignId: campaignId,
      metadataType: 'LMX_POCLMX_COMMERCE',
      metadataCampaignId: campaignId,
      metadataMediaOwnerId: userId,
      source: 'LMX_COMMERCE',
      startDate: startDate || format(new Date(), 'yyyy-MM-dd'), // Fallback to today if no campaign date yet
      endDate: endDate || format(new Date(), 'yyyy-MM-dd'), // Fallback to today if no campaign date yet
      size: 8,
      sort: 'lastModifiedDate,desc',
    },
    skip: !campaignId, // Only skip if no campaignId
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all', // This allows us to see GraphQL errors
  });

  const { data: assertsData, loading: assertsLoading, error: assertsError, refetch: refetchAssets } = useApolloQuery(
    PROOF_OF_PLAY_ASSERTS,
    {
      variables: {
        accessToken: token,
        metadataCampaignId: campaignId,
        metadataMediaOwnerId: userId,
        metadataType: 'LMX_POC',
        source: 'LMX_COMMERCE',
        startDate: startDate || format(new Date(), 'yyyy-MM-dd'), // Use selected date or fallback to today
        endDate: endDate || format(new Date(), 'yyyy-MM-dd'), // Use selected date or fallback to today
        page: 0,
        size: 8,
        sort: 'lastModifiedDate,desc',
        billboardId: selectedBillboard === 'all' ? '' : selectedBillboard,
      },
      skip: !campaignId, // Only skip if no campaignId
      notifyOnNetworkStatusChange: true,
      errorPolicy: 'all', // This allows us to see GraphQL errors
    }
  );

  // Debug Apollo queries (only log when query states change)
  useEffect(() => {
    if (assertsData?.proofOfPlayAssets && assertsData.proofOfPlayAssets.length > 0) {
      // Only log once when assets are first loaded
      console.log('Assets loaded:', assertsData.proofOfPlayAssets.length, 'items');
    }
  }, [assertsData?.proofOfPlayAssets?.length]); // Only log when asset count changes

  // Fallback to mock data for development (only when explicitly enabled)
  const { data: mockProofItems = [], isLoading: mockLoading, error: mockError, refetch: mockRefetch } = useQuery({
    queryKey: ['/api/proof-of-play'],
    queryFn: async () => {
      // Only use mock data when explicitly enabled
      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        const mockResponse = await generateMockData(PROOF_OF_PLAY_ASSERTS);
        return mockResponse.data.proofOfPlayAssets || [];
      }

      // Return empty array if mock data is not enabled
      return [];
    },
    enabled: import.meta.env.VITE_APP_MOCK_DATA === 'true'
  });

  // Calculate days difference for duration
  const calculateDaysDifference = (start: string, end: string): number => {
    const startDate = new Date(start);
    const endDate = new Date(end);

    // Check for invalid dates
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return 0;
    }

    // Reset time portion to ensure accurate day calculation
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    // Add 1 to include both start and end dates
    const timeDifference = endDate.getTime() - startDate.getTime();
    return Math.ceil(timeDifference / (1000 * 3600 * 24)) + 1;
  };

  // Safe date formatting function
  const formatSafeDate = (dateString: string, options?: Intl.DateTimeFormatOptions): string => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return date.toLocaleDateString('en-US', options || {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Data processing effects
  useEffect(() => {
    // Only include mock loading if mock data is enabled
    const includeMockLoading = import.meta.env.VITE_APP_MOCK_DATA === 'true' ? mockLoading : false;
    const shouldShowLoading = (loading && !data) || (assertsLoading && !assertsData) || includeMockLoading;
    setIsLoading(shouldShowLoading);
    setErrorData(error ? error.message : null);
    setErrorAssets(assertsError ? assertsError.message : null);
  }, [loading, assertsLoading, mockLoading, error, assertsError, data, assertsData]);

  // Process campaign data when Apollo data changes
  useEffect(() => {
    if (data?.proofOfPlay) {
      const campaignData = data.proofOfPlay;

      // Handle the date structure from GraphQL response
      const campaignStartDate = campaignData.startDate?.date || campaignData.startDate;
      const campaignEndDate = campaignData.endDate?.date || campaignData.endDate;

      // Update the state with campaign dates for API calls (only if not already set by user)
      if (campaignStartDate && campaignEndDate) {
        const formattedStartDate = campaignStartDate.split('T')[0]; // Extract date part in YYYY-MM-DD format
        const formattedEndDate = campaignEndDate.split('T')[0]; // Extract date part in YYYY-MM-DD format

        // Only set campaign dates if user hasn't selected custom dates
        if (!startDate) setStartDate(formattedStartDate);
        if (!endDate) setEndDate(formattedEndDate);
      }

      const daysCount = calculateDaysDifference(campaignStartDate, campaignEndDate);

      setCampaign({
        id: campaignData.id,
        name: campaignData.name || 'No name available',
        dealId: campaignData.dealId || 'N/A',
        duration: `${daysCount} Days`,
        startDate: campaignStartDate,
        endDate: campaignEndDate
      });

      // Set proof values for summary stats
      setProofValues({
        expected: campaignData.expectedDisplays || 0,
        submitted: campaignData.submittedProofs || 0,
        approved: campaignData.approvedProofs || 0,
        rejected: campaignData.rejectedProofs || 0
      });

      // Process campaign inventories (billboards)
      if (campaignData.campaignInventories) {
        setBillboards(campaignData.campaignInventories.map((inventory: any) => ({
          id: inventory.id,
          inventoryId: inventory.inventoryId,
          inventoryName: inventory.inventoryName,
          inventoryType: inventory.inventoryType,
          mediaOwnerName: inventory.mediaOwnerName || 'N/A',
          mediaOwnerId: inventory.mediaOwnerId || 'N/A',
          packageName: inventory.packageName,
          packageId: inventory.packageId,
        })));
      }
    }
  }, [data, startDate, endDate]); // Added startDate and endDate as dependencies

  // Refetch assets when billboard selection changes
  useEffect(() => {
    if (!campaignId || !refetchAssets) return;

    const timeoutId = setTimeout(() => {
      refetchAssets();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [selectedBillboard, startDate, endDate, campaignId, refetchAssets]);

  // Reset package selection when inventory type changes
  useEffect(() => {
    if (inventoryTypeFilter !== "package") {
      setSelectedPackage("all");
    }
    
    // Reset billboard selection if current selection doesn't match the new filter
    if (inventoryTypeFilter !== "all" && selectedBillboard !== "all") {
      const filteredBillboards = getFilteredBillboards();
      const isCurrentSelectionValid = filteredBillboards.some(billboard => billboard.id === selectedBillboard);
      
      if (!isCurrentSelectionValid) {
        setSelectedBillboard("all");
      }
    }
  }, [inventoryTypeFilter, billboards]);

  // Reset billboard selection when package changes
  useEffect(() => {
    if (inventoryTypeFilter === "package") {
      setSelectedBillboard("all");
    }
  }, [selectedPackage, inventoryTypeFilter]);

  // Process assets data when Apollo assets data changes
  useEffect(() => {
    if (assertsData?.proofOfPlayAssets && assertsData.proofOfPlayAssets.length > 0) {
      const assets = assertsData.proofOfPlayAssets.map((asset: any, index: number) => {
        const isImage = asset.mimeType?.startsWith('image/');
        const isVideo = asset.mimeType?.startsWith('video/');
        return {
          id: asset.id || `asset-${index}`,
          campaignId: campaignId,
          billboardId: asset.metadata?.billboardId || 'N/A',
          creativeId: asset.creativeRefId || asset.masterCreativeId || 'N/A',
          creativeName: asset.fileName || 'Unknown Creative',
          isVideo: asset.mimeType?.startsWith('video/'),
          photoUrl: isImage || isVideo ? asset.filePath : asset.thumbnail, // Use filePath for images/videos, fallback to thumbnail
          previewUrl: asset.thumbnail,
          displayDateTime: asset.createdDate || new Date().toISOString(),
          displayDate: asset.createdDate || new Date().toISOString(),
          uploadDate: asset.createdDate || new Date().toISOString(),
          screenLocation: billboards.find((inv: any) => inv.id === asset.metadata?.billboardId)?.inventoryName || 'Unknown Inventory',
          status: asset.status?.toLowerCase() || 'submitted',
          submittedBy: 'Media Owner',
          uploadedAt: asset.createdDate || new Date().toISOString(),
          reviewedAt: asset.reviewedAt,
          reviewedBy: asset.reviewedBy
        };
      });

      setProofImages(assets);
      setFilteredImages(assets);
    } else if (import.meta.env.VITE_APP_MOCK_DATA === 'true' && mockProofItems && mockProofItems.length > 0) {
      setProofImages(mockProofItems);
      setFilteredImages(mockProofItems);
    } else {
      setProofImages([]);
      setFilteredImages([]);
    }
  }, [assertsData, mockProofItems, billboards, campaignId]);

  // Transform proof images to proof of play items for compatibility
  const proofOfPlayItems: ProofOfPlayItem[] = filteredImages.map((image) => {
    const relatedBillboard = billboards.find(b => b.id === image.billboardId);
    return {
      id: image.id,
      campaignId: image.campaignId || campaignId || '1',
      billboardId: image.billboardId,
      creativeId: image.creativeId,
      creativeName: image.creativeName,
      isVideo: image.isVideo,
      photoUrl: image.photoUrl,
      previewUrl: image.previewUrl,
      displayDateTime: image.displayDateTime,
      screenLocation: image.screenLocation,
      status: image.status as "submitted" | "approved" | "rejected",
      submittedBy: image.submittedBy,
      uploadedAt: image.uploadedAt,
      reviewedAt: image.reviewedAt,
      reviewedBy: image.reviewedBy,
      inventoryType: relatedBillboard?.inventoryType
    };
  });

  // Get campaign data (either from Apollo or mock)
  const currentCampaign = campaign || {
    id: parseInt(campaignId || '1'),
    name: "Loading Campaign...",
    dealId: "Loading...",
    duration: loading ? "Loading..." : (startDate && endDate ? calculateDaysDifference(startDate, endDate) + " Days" : "Loading..."),
    startDate: startDate || format(new Date(), 'yyyy-MM-dd'),
    endDate: endDate || format(new Date(), 'yyyy-MM-dd')
  };

  // Format campaign dates for display
  const formattedCampaignDates = campaign ? {
    startDate: formatSafeDate(campaign.startDate, { day: 'numeric', month: 'short', year: 'numeric' }),
    endDate: formatSafeDate(campaign.endDate, { day: 'numeric', month: 'short', year: 'numeric' })
  } : null;

  // Update proof of play status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status, reviewedBy, comments }: { id: string, status: string, reviewedBy?: string, comments?: string }) => {
      const response = await fetch(`/api/proof-of-play/${id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status, reviewedBy, comments }),
      });
      if (!response.ok) {
        throw new Error('Failed to update status');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/proof-of-play'] });
    },
  });

  // Upload proof of play mutation
  const uploadMutation = useMutation({
    mutationFn: async (proofData: any) => {
      const response = await fetch('/api/proof-of-play', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(proofData),
      });
      if (!response.ok) {
        throw new Error('Failed to upload proof of play');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/proof-of-play'] });
      setShowUploadModal(false);
      setUploadFile(null);
    },
  });

  // Filter billboards based on inventory type
  const getFilteredBillboards = () => {
    if (inventoryTypeFilter === "all") {
      return billboards;
    }
    return billboards.filter(billboard => 
      billboard.inventoryType?.toLowerCase() === inventoryTypeFilter.toLowerCase()
    );
  };

  // Get package inventories when inventory type is package
  const getPackageInventories = (): Record<string, ProofInventory[]> => {
    if (inventoryTypeFilter !== "package") return {};
    
    // Group billboards by package (assuming inventoryName represents package name for package type)
    const packageGroups = billboards
      .filter(b => b.inventoryType?.toLowerCase() === 'package')
      .reduce((groups, billboard) => {
        const packageName = billboard.inventoryName;
        if (!groups[packageName]) {
          groups[packageName] = [];
        }
        groups[packageName].push(billboard);
        return groups;
      }, {} as Record<string, ProofInventory[]>);
    
    return packageGroups;
  };

  // Get inventories for selected package
  const getInventoriesForSelectedPackage = () => {
    const packageInventories = getPackageInventories();
    if (selectedPackage === "all" || !packageInventories[selectedPackage]) {
      return [];
    }
    return packageInventories[selectedPackage];
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "approved":
        return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">✅ Approved</Badge>;
      case "rejected":
        return <Badge className="bg-red-100 text-red-700 border-red-200">❌ Rejected</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-700 border-amber-200">📋 Submitted</Badge>;
    }
  };

  const handleApprove = (item: ProofOfPlayItem) => {
    updateStatusMutation.mutate({
      id: item.id,
      status: "approved",
      reviewedBy: "Campaign Manager"
    });
  };

  const handleReject = (item: ProofOfPlayItem) => {
    setSelectedItemForReject(item);
    setShowRejectModal(true);
  };

  const confirmReject = () => {
    if (selectedItemForReject) {
      updateStatusMutation.mutate({
        id: selectedItemForReject.id,
        status: "rejected",
        reviewedBy: "Campaign Manager",
        comments: rejectReason
      });
    }
    setShowRejectModal(false);
    setRejectReason("");
    setSelectedItemForReject(null);
  };

  const handleUpload = () => {
    if (uploadFile) {
      const proofData = {
        campaignId: selectedCampaign,
        billboardId: selectedBillboard || '1',
        creativeId: `CREATIVE_${Date.now()}`,
        creativeName: uploadFile.name,
        photoUrl: URL.createObjectURL(uploadFile),
        displayDateTime: new Date().toISOString(),
        screenLocation: "User Upload",
        status: "submitted",
        submittedBy: "Media Owner",
        uploadedAt: new Date().toISOString()
      };

      uploadMutation.mutate(proofData);
    }
  };

  // Helper function to check if item is a video
  const isVideo = (item: ProofOfPlayItem): boolean => {
    return !!(item.photoUrl && (
      item.photoUrl.includes('.mp4') ||
      item.photoUrl.includes('.webm') ||
      item.photoUrl.includes('.mov') ||
      item.creativeName?.toLowerCase().includes('.mp4') ||
      item.creativeName?.toLowerCase().includes('.webm') ||
      item.creativeName?.toLowerCase().includes('.mov') ||
      item.photoUrl.includes('gtv-videos-bucket')
    ));
  };

  // Filter items based on media type and inventory type
  const filteredItems = proofOfPlayItems.filter(item => {
    // Media type filter
    let passesMediaFilter = true;
    if (mediaFilter === "images") passesMediaFilter = !item.isVideo;
    if (mediaFilter === "videos") passesMediaFilter = item.isVideo;

    // Inventory type filter  
    let passesInventoryTypeFilter = true;
    if (inventoryTypeFilter !== "all") {
      passesInventoryTypeFilter = item.inventoryType === inventoryTypeFilter;
    }

    return passesMediaFilter && passesInventoryTypeFilter;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredItems.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleImageView = (item: ProofOfPlayItem) => {
    setSelectedImage(item);
    setShowImageModal(true);
  };

  const handleStatusUpdate = (itemId: string, newStatus: "approved" | "rejected") => {
    if (newStatus === "approved") {
      updateStatusMutation.mutate({
        id: itemId,
        status: "approved",
        reviewedBy: "Campaign Manager"
      });
      setShowImageModal(false);
    } else if (newStatus === "rejected") {
      // Find the item and set it for rejection with reason
      const item = proofOfPlayItems.find(item => item.id === itemId);
      if (item) {
        setSelectedItemForReject(item);
        setShowImageModal(false);
        setShowRejectModal(true);
      }
    }
  };

  const handleDownloadAll = () => {
    // In real app, this would generate and download a ZIP file
    // console.log("Downloading all proof of play images");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[95vw] mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-bold text-emerald-600">Proof of Play</h1>
            <div className="flex items-center space-x-3">
              <Button onClick={handleDownloadAll} variant="outline">
                <Download className="h-4 w-4 mr-2" />
                Download All
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  window.location.href = `${window.location.origin}${base_path}/my-campaigns`;
                }}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Campaigns
              </Button>
            </div>
        </div>

        <div className="mb-6">
          <p className="text-gray-600">Track and verify when and where content was displayed</p>
        </div>

        {/* Campaign Info */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg text-emerald-600">Campaign Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Campaign name</span>
                <p className="font-medium">{currentCampaign.name}</p>
              </div>
              <div>
                <span className="text-gray-600">Deal ID</span>
                <p className="font-medium">{currentCampaign.dealId}</p>
              </div>
              <div>
                <span className="text-gray-600">Duration</span>
                <p className="font-medium">{currentCampaign.duration}</p>
              </div>
              <div>
                <span className="text-gray-600">Campaign Period</span>
                <p className="font-medium">
                  {campaign
                    ? `${formatDate(campaign.startDate).replace('Sept', 'Sep')} - ${formatDate(campaign.endDate).replace('Sept', 'Sep')}`
                    : 'Loading...'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Loading and Error States */}
        {isLoading && (
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="flex items-center justify-center">
                <div className="text-gray-600">Loading campaign data...</div>
              </div>
            </CardContent>
          </Card>
        )}

        {(errorData || errorAssets) && (
          <Card className="mb-6 border-red-200 bg-red-50">
            <CardContent className="p-6">
              <div className="text-red-600">
                <p className="font-medium">Error loading data:</p>
                {errorData && <p>Campaign: {errorData}</p>}
                {errorAssets && <p>Assets: {errorAssets}</p>}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary Stats
        {proofValues && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg text-emerald-600">Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Expected Displays</span>
                  <p className="font-medium text-emerald-600">{proofValues.expected}</p>
                </div>
                <div>
                  <span className="text-gray-600">Submitted Proofs</span>
                  <p className="font-medium text-blue-600">{proofValues.submitted}</p>
                </div>
                <div>
                  <span className="text-gray-600">Approved Proofs</span>
                  <p className="font-medium text-green-600">{proofValues.approved}</p>
                </div>
                <div>
                  <span className="text-gray-600">Rejected Proofs</span>
                  <p className="font-medium text-red-600">{proofValues.rejected}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )} */}

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg text-emerald-600">Proof of play</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`grid grid-cols-1 gap-4 ${inventoryTypeFilter === "package" ? "md:grid-cols-6" : "md:grid-cols-5"}`}>
              <div>
                <Label htmlFor="inventory-type-filter" className="text-sm font-medium text-gray-700 border-emerald-200 focus:border-emerald-500">
                  Inventory Type
                </Label>
                <Select value={inventoryTypeFilter} onValueChange={setInventoryTypeFilter}>
                  <SelectTrigger id="inventory-type-filter" className="border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none transition-all duration-200 hover:border-gray-300">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-emerald-200">
                    <SelectItem value="all">All Types</SelectItem>
                    {Array.from(new Set(billboards.map(b => b.inventoryType))).map((type) => (
                      <SelectItem key={type} value={type}>
                        <div className="flex items-center space-x-2">
                          <div className={`w-2 h-2 rounded-full ${type.toLowerCase().includes('digital') ? 'bg-blue-500' :
                            (type.toLowerCase().includes('classic') || type.toLowerCase().includes('static')) ? 'bg-green-500' :
                              type.toLowerCase().includes('package') ? 'bg-purple-500' :
                                type === 'transit' ? 'bg-orange-500' :
                                  'bg-gray-500'
                            }`}></div>
                          <span className="capitalize">{_inventoryTypeDisplayMapping[type.toLowerCase()] || type.toLowerCase()}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {inventoryTypeFilter === "package" ? (
                <>
                  <div>
                    <Label htmlFor="package-select" className="text-sm font-medium text-gray-700">
                      Select Package
                    </Label>
                    <Select value={selectedPackage} onValueChange={setSelectedPackage}>
                      <SelectTrigger id="package-select" className="border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none transition-all duration-200 hover:border-gray-300">
                        <SelectValue placeholder="All Packages" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="all">All Packages</SelectItem>
                        {Array.from(new Set(
                          billboards
                            .filter(b => b.inventoryType?.toLowerCase() === 'package')
                            .map(b => b.packageName)
                            .filter((name): name is string => typeof name === 'string' && name.length > 0)
                        )).map((packageName) => (
                          <SelectItem key={packageName} value={packageName}>
                            {packageName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="billboard-select" className="text-sm font-medium text-gray-700">
                      Select Inventory
                    </Label>
                    <Select 
                      value={selectedBillboard} 
                      onValueChange={setSelectedBillboard}
                    >
                      <SelectTrigger 
                        id="billboard-select" 
                        className="border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none transition-all duration-200 hover:border-gray-300"
                      >
                        <SelectValue placeholder="All Inventories" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="all">All Inventories</SelectItem>
                        {selectedPackage === "all" ? (
                          // Show all package inventories when "All Packages" is selected
                          billboards
                            .filter(b => b.inventoryType?.toLowerCase() === 'package')
                            .map((billboard) => (
                              <SelectItem key={billboard.id} value={billboard.id}>
                                {billboard.inventoryName}
                              </SelectItem>
                            ))
                        ) : (
                          // Show only inventories belonging to the selected package
                          billboards
                            .filter(b => {
                              const selectedPackageInventory = billboards.find(
                                inv => inv.inventoryType?.toLowerCase() === 'package' && inv.packageName === selectedPackage
                              );
                              return b.inventoryType?.toLowerCase() === 'package' && b.packageId === selectedPackageInventory?.packageId;
                            })
                            .map((billboard) => (
                              <SelectItem key={billboard.id} value={billboard.id}>
                                {billboard.inventoryName}
                              </SelectItem>
                            ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              ) : (
                <div>
                  <Label htmlFor="billboard-select" className="text-sm font-medium text-gray-700">
                    Select Inventory
                  </Label>
                  <Select value={selectedBillboard} onValueChange={setSelectedBillboard}>
                    <SelectTrigger id="billboard-select" className="border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none transition-all duration-200 hover:border-gray-300">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="all">All</SelectItem>
                      {getFilteredBillboards().map((billboard) => (
                        <SelectItem key={billboard.id} value={billboard.id}>
                          {billboard.inventoryName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label htmlFor="media-filter" className="text-sm font-medium text-gray-700">
                  Media Type
                </Label>
                <Select value={mediaFilter} onValueChange={(value) => setMediaFilter(value as "all" | "images" | "videos")}>
                  <SelectTrigger id="media-filter" className="border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none transition-all duration-200 hover:border-gray-300">
                    <SelectValue placeholder="All Media" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="all">All Media</SelectItem>
                    <SelectItem value="images">Images Only</SelectItem>
                    <SelectItem value="videos">Videos Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="start-date" className="text-sm font-medium text-gray-700">
                  Duration
                </Label>
                <div className="flex items-center space-x-2">
                  <Input
                    id="start-date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    min={campaign?.startDate ? campaign.startDate.split('T')[0] : undefined}
                    max={campaign?.endDate ? campaign.endDate.split('T')[0] : undefined}
                    className="border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none transition-all duration-200 hover:border-gray-300 flex-1"
                  />
                  <span className="text-gray-500">to</span>
                  <Input
                    id="end-date"
                    type="date"
                    value={endDate}
                    min={campaign?.startDate ? campaign.startDate.split('T')[0] : undefined}
                    max={campaign?.endDate ? campaign.endDate.split('T')[0] : undefined}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sites Count */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-emerald-600 mb-4">Choose from {filteredItems.length} sites</h3>
        </div>

        {/* Loading and Error States */}
        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            <p className="text-gray-600 ml-3">Loading proof of play data...</p>
          </div>
        )}

        {error && (
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">Error loading proof of play data.</p>
            <Button onClick={() => refetch()} variant="outline">
              Try Again
            </Button>
          </div>
        )}

        {/* Proof of Play Grid */}
        {!isLoading && !error && (
          <>
            {currentItems.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {currentItems.map((item) => (
                  <Card key={item.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="relative w-full aspect-video bg-gray-100">
                      {/* {isVideo(item) ? (
                        <video
                          src={item.previewUrl}
                          className="absolute inset-0 w-full h-full object-cover"
                          muted
                          preload="metadata"
                        />
                      ) : (
                        <img
                          src={item.photoUrl}
                          alt={item.creativeName}
                          className="absolute inset-0 w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.src = '/nomedia.jpg'; }}
                        />
                      )} */}
                      <img
                        src={item?.isVideo ? item.previewUrl : item.photoUrl}
                        alt={item.creativeName}
                        className="absolute inset-0 w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.src = '/nomedia.jpg'; }}
                      />
                      <div className="absolute top-3 right-3">
                        {getStatusBadge(item.status)}
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-1" 
                            title={item.screenLocation.length > 50 ? item.screenLocation : ''}>
                              {item.screenLocation.length > 50
                              ? item.screenLocation.substring(0, 50) + '...'
                              : item.screenLocation
                            }
                              </h4>
                          <p className="text-xs text-emerald-600 font-medium mt-2">
                            By {(() => {
                              const billboard = billboards.find(b => b.id === item.billboardId);
                              const isPackage = item.inventoryType?.toLowerCase() === 'package' || billboard?.inventoryType?.toLowerCase() === 'package';
                              return isPackage ? 'Multiple Media Owners' : (billboard?.mediaOwnerName || 'N/A');
                            })()}
                          </p>
                          <p className="text-sm text-gray-600 mt-2">
                            Uploaded on {formatDate(item.uploadedAt).replace('Sept', 'Sep')}
                          </p>
                        </div>

                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <Calendar className="h-4 w-4 text-emerald-600" />
                          <span>
                            {new Date(item.displayDateTime).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',  
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            }).replace('Sept', 'Sep')}
                          </span>
                        </div>

                        <div className="flex items-center space-x-2 text-sm text-gray-600 min-w-0">
                          {item?.isVideo ? (
                            <svg className="h-4 w-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          ) : (
                            <ImageIcon className="h-4 w-4 flex-shrink-0" />
                          )}
                          <span 
                            className="truncate"
                            title={item.creativeName.length > 50 ? item.creativeName : ''}
                          >
                            {item.creativeName.length > 50
                              ? item.creativeName.substring(0, 50) + '...'
                              : item.creativeName
                            }
                          </span>
                        </div>

                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <MapPin className="h-4 w-4 flex-shrink-0" />
                          <span className="break-words"
                            title={item.screenLocation.length > 60 ? item.screenLocation : ''} >
                            {item.screenLocation.length > 60
                              ? item.screenLocation.substring(0, 60) + '...'
                              : item.screenLocation
                            }
                          </span>
                        </div>

                        {item.inventoryType && (
                          <div className="flex items-center">
                            <Badge className={`text-xs px-2 py-1 ${item.inventoryType.toLowerCase().includes('digital') ? 'bg-blue-100 text-blue-700 border-blue-200' :
                              (item.inventoryType.toLowerCase().includes('classic') || item.inventoryType.toLowerCase().includes('static')) ? 'bg-green-100 text-green-700 border-green-200' :
                                item.inventoryType.toLowerCase().includes('package') ? 'bg-purple-100 text-purple-700 border-purple-200' :
                                  item.inventoryType.toLowerCase().includes('transit') ? 'bg-orange-100 text-orange-700 border-orange-200' :
                                    'bg-gray-100 text-gray-700 border-gray-200'
                              }`}>
                              <div className={`w-2 h-2 rounded-full mr-1 ${item.inventoryType.toLowerCase().includes('digital') ? 'bg-blue-500' :
                                (item.inventoryType.toLowerCase().includes('classic') || item.inventoryType.toLowerCase().includes('static')) ? 'bg-green-500' :
                                  item.inventoryType.toLowerCase().includes('package') ? 'bg-purple-500' :
                                    item.inventoryType.toLowerCase().includes('transit') ? 'bg-orange-500' :
                                      'bg-gray-500'
                                }`}></div>
                              <span className="capitalize">{_inventoryTypeDisplayMapping[item.inventoryType.toLowerCase()] || item.inventoryType.toLowerCase()}</span>
                            </Badge>
                          </div>
                        )}

                        <div className="flex space-x-2 pt-2">
                          <Button
                            onClick={() => {
                              setSelectedImage(item);
                              setShowImageModal(true);
                            }}
                            size="sm"
                            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>

                          {item.status === "submitted" && (
                            <>
                              <Button
                                onClick={() => handleApprove(item)}
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                disabled={updateStatusMutation.isPending}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                              <Button
                                onClick={() => handleReject(item)}
                                size="sm"
                                className="bg-red-600 hover:bg-red-700 text-white"
                                disabled={updateStatusMutation.isPending}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="mb-6">
                <CardContent className="p-6">
                  <div className="text-center py-8">
                    <div className="flex justify-center mb-4">
                      <ImageIcon className="h-16 w-16 text-gray-300" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Proof of Play Data</h3>
                    <p className="text-gray-600 mb-4">
                      No proof of play images or videos have been submitted for this campaign yet.
                    </p>
                    {import.meta.env.VITE_APP_MOCK_DATA !== 'true' && (
                      <p className="text-sm text-gray-500">
                        Check if the campaign has any submitted proofs or try adjusting your filters.
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Pagination */}
        {!isLoading && !error && totalPages > 1 && (
          <div className="flex justify-center items-center space-x-4 mt-8">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <ChevronLeftIcon className="h-4 w-4 mr-1" />
              Previous
            </Button>

            <div className="flex items-center space-x-2">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <Button
                  key={page}
                  variant={currentPage === page ? "default" : "outline"}
                  size="sm"
                  onClick={() => handlePageChange(page)}
                  className="w-8 h-8 p-0"
                >
                  {page}
                </Button>
              ))}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}

        {/* Upload Modal */}
        <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-emerald-600">Upload Proof of Play</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="creative-name">Creative Name</Label>
                <Input id="creative-name" placeholder="Enter creative name" />
              </div>
              <div>
                <Label htmlFor="screen-location">Screen Location</Label>
                <Input id="screen-location" placeholder="Enter screen location" />
              </div>
              <div>
                <Label htmlFor="display-datetime">Display Date & Time</Label>
                <Input id="display-datetime" type="datetime-local" />
              </div>
              <div>
                <Label htmlFor="photo-upload">Photo/Video</Label>
                <Input
                  id="photo-upload"
                  type="file"
                  accept="image/*,video/*"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                />
              </div>
              <div>
                <Label htmlFor="comments">Comments (Optional)</Label>
                <Textarea id="comments" placeholder="Add any comments..." />
              </div>
              <div className="flex space-x-2">
                <Button
                  onClick={() => setShowUploadModal(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpload}
                  className="flex-1 btn-emerald-gradient"
                  disabled={!uploadFile || uploadMutation.isPending}
                >
                  {uploadMutation.isPending ? "Uploading..." : "Upload"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Rejection Modal */}
        <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-600">Reject Proof of Play</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Please provide a reason for rejecting this proof of play submission:
              </p>
              <Textarea
                placeholder="Enter rejection reason..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="min-h-[80px]"
              />
              <div className="flex space-x-2">
                <Button
                  onClick={() => setShowRejectModal(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmReject}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  disabled={!rejectReason.trim() || updateStatusMutation.isPending}
                >
                  {updateStatusMutation.isPending ? "Rejecting..." : "Reject"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Image View Modal */}
        <Dialog open={showImageModal} onOpenChange={setShowImageModal}>
          <DialogContent className="max-w-[95vw] max-h-[95vh] sm:max-w-4xl p-0">
            <div className="flex flex-col h-full max-h-[95vh]">
              <DialogHeader className="flex-shrink-0 bg-white border-b p-6 flex justify-start">
                <DialogTitle className="text-emerald-600 text-left">
                  {selectedImage?.screenLocation}
                </DialogTitle>
              </DialogHeader>
              {selectedImage && (
                <div className="flex-1 overflow-y-auto p-6">
                  <div className="bg-gray-100 rounded-lg overflow-hidden flex justify-center items-center" style={{ minHeight: 320 }}>
                    <div className="w-full max-w-2xl aspect-video bg-black flex items-center justify-center">
                      {selectedImage.isVideo ? (
                        <video
                          src={selectedImage.photoUrl}
                          className="w-full h-full object-contain bg-black"
                          controls
                          preload="metadata"
                          style={{ background: 'black' }}
                        />
                      ) : (
                        <img
                          src={selectedImage.photoUrl}
                          alt={selectedImage.creativeName}
                          className="w-full h-full object-contain bg-black"
                          style={{ background: 'black' }}
                        />
                      )}
                    </div>
                  </div>

                  <div className="space-y-4 mt-4">
                    <div className="flex justify-between items-center text-sm border rounded-md bg-white px-4 py-3">
                      {/* Creative */}
                      <div>
                        <span className="text-gray-600 mr-1">Creative:</span>
                        <span 
                          className="font-medium mr-1truncate max-w-xs inline-block"
                          title={selectedImage.creativeName.length > 30 ? selectedImage.creativeName : undefined}
                        >
                          {selectedImage.creativeName.length > 30 
                            ? selectedImage.creativeName.substring(0, 30) + '...' 
                            : selectedImage.creativeName
                          }
                        </span>
                      </div>

                      {/* Uploaded on */}
                      <div>
                        <span className="text-gray-600 mr-1">Uploaded on:</span>
                        <span className="font-medium">{formatDate(selectedImage.uploadedAt).replace('Sept', 'Sep')}</span>
                      </div>

                      {/* Status */}
                      <div className="flex items-center">
                        <span className="text-gray-600 mr-1">Status:</span>
                        {getStatusBadge(selectedImage.status)}
                      </div>
                    </div>


                    {selectedImage.comments && (
                      <div>
                        <span className="text-gray-600">Comments</span>
                        <p className="mt-1 text-sm bg-gray-50 p-3 rounded">{selectedImage.comments}</p>
                      </div>
                    )}

                    <div className="flex justify-between items-center">
                      <div className="flex space-x-2">
                        {selectedImage.status === "submitted" && (
                          <>
                            <Button
                              onClick={() => handleStatusUpdate(selectedImage.id, "approved")}
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              onClick={() => handleStatusUpdate(selectedImage.id, "rejected")}
                              size="sm"
                              className="bg-red-600 hover:bg-red-700 text-white"
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Reject
                            </Button>
                          </>
                        )}
                      </div>
                      <Button
                        onClick={() => setShowImageModal(false)}
                        variant="outline"
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}